import { useState, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { UploadButton } from './UploadButton';
import { Link } from 'react-router-dom';

export const OgImageUpload = () => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadOgImage = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      if (!event.target.files || event.target.files.length === 0) {
        throw new Error('Bitte wählen Sie eine Datei aus');
      }

      setUploading(true);
      const file = event.target.files[0];
      
      if (file.size > 5 * 1024 * 1024) {
        throw new Error('Die Datei darf nicht größer als 5MB sein');
      }

      if (!file.type.startsWith('image/')) {
        throw new Error('Bitte wählen Sie nur Bilddateien aus');
      }
      
      toast.loading('Social Media Vorschaubild wird hochgeladen...', { duration: 10000 });
      
      // Formular für die Edge Function vorbereiten
      const formData = new FormData();
      formData.append('file', file);
      
      console.log('Uploading OG image via Edge Function...');
      
      // Rufe die Supabase Edge Function auf
      const { data, error } = await supabase.functions.invoke('upload-og-image', {
        body: formData
      });
      
      if (error) {
        console.error('Edge function error:', error);
        throw new Error(error.message || 'Fehler beim Hochladen des Bildes');
      }

      console.log('Edge function response:', data);
      toast.dismiss();
      
      if (data && data.publicUrl) {
        toast.success('Social Media Vorschaubild wurde aktualisiert');
        toast.info('Es kann bis zu 24 Stunden dauern, bis die Änderungen auf Social Media sichtbar werden (Facebook Cache)');
        
        // Facebook Cache-Aktualisierung empfehlen
        toast.info(
          'Tipp: Verwenden Sie das Facebook Debug Tool, um den Cache zu aktualisieren',
          {
            action: {
              label: 'Debug Tool öffnen',
              onClick: () => window.open('https://developers.facebook.com/tools/debug/', '_blank')
            },
            duration: 10000
          }
        );
        
        // Löse ein Event aus, um den Index.tsx zu informieren
        window.dispatchEvent(new CustomEvent('og-image-updated', { 
          detail: { 
            publicUrl: data.publicUrl,
            fileName: data.fileName,
            cacheBuster: data.cacheBuster
          }
        }));
        
        // Wichtig: Aktualisiere Metadaten im Head-Bereich für sofortiges Testen
        const ogImageMeta = document.querySelector('meta[property="og:image"]');
        const twitterImageMeta = document.querySelector('meta[property="twitter:image"]');
        
        if (ogImageMeta) {
          ogImageMeta.setAttribute('content', `${data.publicUrl}?t=${data.cacheBuster}`);
        } else {
          // Wenn der Meta-Tag nicht existiert, erstelle ihn
          const newOgImageMeta = document.createElement('meta');
          newOgImageMeta.setAttribute('property', 'og:image');
          newOgImageMeta.setAttribute('content', `${data.publicUrl}?t=${data.cacheBuster}`);
          document.head.appendChild(newOgImageMeta);
        }
        
        if (twitterImageMeta) {
          twitterImageMeta.setAttribute('content', `${data.publicUrl}?t=${data.cacheBuster}`);
        } else {
          // Wenn der Meta-Tag nicht existiert, erstelle ihn
          const newTwitterImageMeta = document.createElement('meta');
          newTwitterImageMeta.setAttribute('property', 'twitter:image');
          newTwitterImageMeta.setAttribute('content', `${data.publicUrl}?t=${data.cacheBuster}`);
          document.head.appendChild(newTwitterImageMeta);
        }
        
        // Refresh der Seite nach einer Verzögerung für ein besseres UX
        setTimeout(() => {
          window.location.reload();
        }, 3000);
      } else {
        throw new Error('Die Edge Function hat keine gültige Antwort zurückgegeben');
      }
      
      // Für bessere UX - zeige Nutzern, dass Bild wurde erfolgreich hochgeladen
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.message || 'Fehler beim Hochladen des Bildes');
    } finally {
      setUploading(false);
    }
  };

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className="space-y-4">
      <UploadButton uploading={uploading} onUploadClick={handleUploadClick} />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={uploadOgImage}
        disabled={uploading}
        className="hidden"
      />
      <div className="mt-4 text-sm text-gray-600">
        <Link to="/admin/meta-tags" className="text-blue-500 hover:underline">
          Meta-Tags anzeigen und prüfen
        </Link>
      </div>
    </div>
  );
};
